import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import CustomerVoiceSection from '@/components/feature/CustomerVoiceSection';

const cohorts = [
  {
    num: '01',
    role: '架构师 / 研发负责人',
    required: '必选',
    focus: [
      '把为人工准备的研发环境改造成为 AI 准备的研发环境',
      '跨越前后端 / 测试 / 部署的端到端工作方式',
      'AI 提效后什么样的软技能成为新瓶颈',
    ],
    icon: 'ri-compass-3-line',
    accent: '#2563EB',
  },
  {
    num: '02',
    role: '研发工程师',
    required: '必选',
    focus: [
      '在治理好的环境中快速上手 AI 协作',
      '学会有效地与 AI 交互、理解能力边界',
      '实际项目中的操作规范与流水线',
    ],
    icon: 'ri-code-line',
    accent: '#F97316',
  },
  {
    num: '03',
    role: '测试团队',
    required: '必选',
    focus: [
      '利用新环境快速搭建自动化测试体系',
      '业务规则反推 + 接口异常路径补全',
      'AI 辅助测试的适用范围与策略',
    ],
    icon: 'ri-test-tube-line',
    accent: '#2563EB',
  },
  {
    num: '04',
    role: 'IT 团队',
    required: '可选',
    focus: [
      '把运维、部署、监控等流程自动化',
      'AI 协助下的环境维护与故障定位',
      '与研发流水线的对接',
    ],
    icon: 'ri-server-line',
    accent: '#64748B',
  },
  {
    num: '05',
    role: '产品团队',
    required: '可选',
    focus: [
      '与提效后的研发团队协作方式的转变',
      '需求拆解、规则文档化与 AI 可读化',
      'AI 协作下的产研节奏调整',
    ],
    icon: 'ri-lightbulb-flash-line',
    accent: '#64748B',
  },
];

const engagements = [
  {
    id: 'deep',
    label: '深度工作坊',
    sub: 'Deep Workshop',
    tag: '建立标杆',
    icon: 'ri-focus-3-line',
    accent: '#F97316',
    fit: '想把一个真实项目作为标杆打透，建立内部样板',
    team: '5 人以内 / 单批',
    cycle: '10 个工作日现场 + 16h 远程支持',
    output: [
      '完整 Skills / Rules / Agents 配置',
      '业务规则反推文档体系',
      '端到端 AI 工作流脚本',
      '可迁移到其他项目的资产清单',
    ],
  },
  {
    id: 'broad',
    label: '普及培训',
    sub: 'Broad Training',
    tag: '统一认知',
    icon: 'ri-group-line',
    accent: '#2563EB',
    fit: '想在跨多项目 / 多团队范围内统一认知和分工',
    team: '20+ 人 / 多批',
    cycle: '5–10 天集中开展（可分批）',
    output: [
      '研发体系 AI 化方法体系',
      '样板项目演示与代码走读',
      '分层培训手册与角色分工建议',
      '关键 Skills / Rules 样板',
    ],
  },
];

const notTraditional = [
  {
    bad: '课堂讲 AI 工具怎么用',
    good: '在你的真实项目里直接用',
  },
  {
    bad: '给你一套通用方法论',
    good: '根据你的技术栈定制工作流',
  },
  {
    bad: '培训结束就结束',
    good: '建立团队自己的 AI 工程资产',
  },
  {
    bad: '讲师讲，学员听',
    good: '我们一起写代码、一起调试',
  },
];

const outcomes = [
  { icon: 'ri-speed-line', title: '核心研发任务人效提升', value: '2–3×', desc: '团队在核心研发任务上通常可以获得 2–3 倍的人效提升。具体幅度因项目复杂度、团队基础和模块特征不同而异，效果在培训过程中即可见。' },
  { icon: 'ri-tools-line', title: '工程资产可复用', value: '可复用', desc: '每次带教都会形成团队自己的 CLAUDE.md、Skills、Rules、流水线规范与文档体系，跨项目可直接迁移。' },
  { icon: 'ri-team-line', title: '能力真正迁移', value: '留得住', desc: '不依赖外部顾问，团队自己能持续运转 AI 协作工作流，并具备向其他产品线复制的能力。' },
];

function FadeIn({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className="transition-all duration-700"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

export default function TrainingPage() {
  const [activeMode, setActiveMode] = useState(engagements[0].id);
  const current = engagements.find((e) => e.id === activeMode) ?? engagements[0];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: '\'Noto Sans SC\', Inter, sans-serif' }}>
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-24 bg-[#0F172A] relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/bg-tech-dark.svg"
            alt=""
            className="w-full h-full object-cover object-top opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/90 to-[#0F172A]/70" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#2563EB]/40 text-[#2563EB] text-xs mb-6">
              <i className="ri-graduation-cap-line" />
              AI Enablement
            </div>
            <h1
              className="text-4xl md:text-6xl font-bold text-white leading-tight mb-6"
              style={{ letterSpacing: '-0.02em' }}
            >
              帮助你的团队<br />
              <span className="text-[#F97316]">真正用上 AI</span>。
            </h1>
            <p className="text-lg text-white/55 leading-relaxed mb-10 max-w-xl">
              不是工具培训，不是概念讲座。<br />
              是在你的真实项目里，手把手完成能力迁移。
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="px-7 py-3.5 rounded-full bg-[#F97316] text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-[#ea6c0a] transition-colors"
              >
                预约免费诊断
              </Link>
              <Link
                to="/#premise"
                className="px-7 py-3.5 rounded-full border border-white/20 text-white text-sm font-medium whitespace-nowrap cursor-pointer hover:border-white/40 transition-colors"
              >
                了解方法论
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Not Traditional Training */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <FadeIn>
            <div className="text-center mb-12">
              <span className="text-xs uppercase tracking-widest text-[#64748B] mb-3 block" style={{ fontFamily: 'Inter, sans-serif' }}>
                What Makes Us Different
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A]" style={{ letterSpacing: '-0.02em' }}>
                这不是传统培训
              </h2>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {notTraditional.map((item, idx) => (
              <FadeIn key={item.bad} delay={idx * 70}>
                <div className="bg-[#F8FAFC] rounded-2xl p-6 border border-slate-100 h-full">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-close-line text-sm text-[#94A3B8]" />
                    </div>
                    <p className="text-xs text-[#94A3B8] line-through">{item.bad}</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-check-line text-sm text-[#F97316]" />
                    </div>
                    <p className="text-sm font-semibold text-[#0F172A]">{item.good}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum — 一核 + 五批 */}
      <section className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <FadeIn>
            <div className="max-w-3xl mb-12">
              <span className="text-xs uppercase tracking-widest text-[#64748B] mb-3 block" style={{ fontFamily: 'Inter, sans-serif' }}>
                Curriculum
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-5" style={{ letterSpacing: '-0.02em' }}>
                一个核心产品 · 五批分层
              </h2>
              <p className="text-base text-[#64748B] leading-relaxed">
                核心产品就是「研发体系 AI 化赋能」一个综合工作坊。内部按角色分批进入，每批 5 人以内、内容聚焦该批最相关的实操场景。
              </p>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {cohorts.map((c, idx) => (
              <FadeIn key={c.num} delay={idx * 70}>
                <div className="bg-white rounded-2xl p-6 border border-slate-100 h-full flex flex-col">
                  <div className="flex items-center justify-between mb-5">
                    <div
                      className="w-10 h-10 flex items-center justify-center rounded-xl"
                      style={{ backgroundColor: `${c.accent}15` }}
                    >
                      <i className={`${c.icon} text-lg`} style={{ color: c.accent }} />
                    </div>
                    <span
                      className="text-xs px-2.5 py-1 rounded-full font-medium"
                      style={{
                        backgroundColor: c.required === '必选' ? '#F9731615' : '#94A3B815',
                        color: c.required === '必选' ? '#F97316' : '#64748B',
                      }}
                    >
                      {c.required}
                    </span>
                  </div>
                  <p className="text-xs font-bold text-[#2563EB]/40 mb-1.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                    第 {c.num.replace(/^0/, '')} 批
                  </p>
                  <h3 className="text-base font-bold text-[#0F172A] mb-4">{c.role}</h3>
                  <ul className="flex flex-col gap-2.5 mt-auto">
                    {c.focus.map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-sm text-[#F97316] mt-0.5 shrink-0" />
                        <span className="text-xs text-[#64748B] leading-relaxed">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={400}>
            <p className="mt-8 text-sm text-[#64748B]/80 leading-relaxed text-center">
              每批人数控制在 5 人以内，保证实操效果。架构师批通常必选——它是后续工程师批、测试批能否落地的前置条件。
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Engagement Models — 两种合作模式 */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <FadeIn>
            <div className="max-w-3xl mb-10">
              <span className="text-xs uppercase tracking-widest text-[#64748B] mb-3 block" style={{ fontFamily: 'Inter, sans-serif' }}>
                Engagement Models
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-5" style={{ letterSpacing: '-0.02em' }}>
                两种合作模式
              </h2>
              <p className="text-base text-[#64748B] leading-relaxed">
                首次沟通里我们会先判断你的真实需求，再决定走深度工作坊还是普及培训。两种节奏的目标不同，定价和投入也不同，不能用同一把尺子衡量。
              </p>
            </div>

            <div className="flex flex-wrap gap-3 mb-10">
              {engagements.map((e) => (
                <button
                  key={e.id}
                  onClick={() => setActiveMode(e.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    activeMode === e.id
                      ? 'bg-[#0F172A] text-white'
                      : 'bg-[#F8FAFC] text-[#64748B] border border-slate-100 hover:border-slate-200'
                  }`}
                >
                  <i className={`${e.icon} text-sm`} />
                  {e.label}
                </button>
              ))}
            </div>
          </FadeIn>

          <FadeIn delay={100}>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              <div className="lg:col-span-2">
                <div className="bg-[#F8FAFC] rounded-2xl p-8 border border-slate-100 h-full flex flex-col">
                  <div className="flex items-start justify-between mb-6">
                    <div
                      className="w-14 h-14 flex items-center justify-center rounded-2xl"
                      style={{ backgroundColor: `${current.accent}15` }}
                    >
                      <i className={`${current.icon} text-2xl`} style={{ color: current.accent }} />
                    </div>
                    <span
                      className="text-xs font-semibold px-3 py-1 rounded-full"
                      style={{ backgroundColor: `${current.accent}15`, color: current.accent }}
                    >
                      {current.tag}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-[#0F172A] mb-1">{current.label}</h3>
                  <p className="text-xs text-[#64748B] mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {current.sub}
                  </p>

                  <div className="mb-5">
                    <p className="text-xs uppercase tracking-widest text-[#64748B] mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                      适合谁
                    </p>
                    <p className="text-sm text-[#0F172A] leading-relaxed">{current.fit}</p>
                  </div>

                  <div className="mb-5">
                    <p className="text-xs uppercase tracking-widest text-[#64748B] mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                      团队规模
                    </p>
                    <p className="text-sm text-[#0F172A] leading-relaxed">{current.team}</p>
                  </div>

                  <div className="mt-auto pt-5 border-t border-slate-100">
                    <p className="text-xs uppercase tracking-widest text-[#64748B] mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                      周期
                    </p>
                    <p className="text-sm text-[#0F172A] leading-relaxed">{current.cycle}</p>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-3 flex flex-col gap-5">
                <div className="bg-[#F8FAFC] rounded-2xl p-7 border border-slate-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: `${current.accent}15` }}>
                      <i className="ri-archive-line text-sm" style={{ color: current.accent }} />
                    </div>
                    <span className="text-sm font-semibold text-[#0F172A]">交付物清单</span>
                  </div>
                  <ul className="flex flex-col gap-3">
                    {current.output.map((o) => (
                      <li key={o} className="flex items-start gap-3">
                        <i className="ri-check-line text-sm mt-0.5 shrink-0" style={{ color: current.accent }} />
                        <span className="text-sm text-[#0F172A] leading-relaxed">{o}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-[#0F172A] rounded-2xl p-7">
                  <p className="text-xs uppercase tracking-widest text-white/40 mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Sales Note
                  </p>
                  <p className="text-sm text-white/70 leading-relaxed mb-5">
                    选错模式比不做更糟——把"普及培训"的预期套在"深度工作坊"上，会让团队觉得没拿到方法体系；把"深度工作坊"的预期套在"普及培训"上，会让团队觉得没拿到标杆项目。所以正式合作前会有一次免费诊断。
                  </p>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#F97316] hover:text-[#ea6c0a] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    预约免费诊断
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Outcomes */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <FadeIn>
            <div className="text-center mb-14">
              <span className="text-xs uppercase tracking-widest text-[#64748B] mb-3 block" style={{ fontFamily: 'Inter, sans-serif' }}>Outcomes</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A]" style={{ letterSpacing: '-0.02em' }}>
                带教结束后，你会得到什么
              </h2>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {outcomes.map((item, idx) => (
              <FadeIn key={item.title} delay={idx * 80}>
                <div className="bg-[#F8FAFC] rounded-2xl p-8 border border-slate-100 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#F97316]/10 mx-auto mb-4">
                    <i className={`${item.icon} text-xl text-[#F97316]`} />
                  </div>
                  <p className="text-3xl font-bold text-[#0F172A] mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>{item.value}</p>
                  <p className="text-sm font-semibold text-[#0F172A] mb-3">{item.title}</p>
                  <p className="text-sm text-[#64748B] leading-relaxed">{item.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Voices */}
      <CustomerVoiceSection
        variant="gray"
        eyebrow="Customer Voice"
        title="参训团队真实反馈"
        subtitle="完成培训的客户团队按角色匿名反馈，可作为评估实施效果的参考。"
      />

      {/* Proof: We Practice What We Preach */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <FadeIn>
            <div className="bg-[#0F172A] rounded-2xl p-10 md:p-14 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <p className="text-xs uppercase tracking-widest text-white/40 mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>Why Trust Us</p>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-5" style={{ letterSpacing: '-0.01em' }}>
                  我们自己就是这套方法的实践者。
                </h2>
                <p className="text-base text-white/55 leading-relaxed mb-6">
                  宽思智能不只是培训机构。我们同时用这套方法为客户直接交付软件。培训内容来自真实项目的一线经验，不是二手知识。
                </p>
                <Link
                  to="/software-services"
                  className="inline-flex items-center gap-2 text-sm font-medium text-[#F97316] hover:text-[#ea6c0a] transition-colors cursor-pointer whitespace-nowrap"
                >
                  了解我们的软件服务
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { num: '5 人 / 批', label: '小班实操，深度优先' },
                  { num: '10+', label: '团队定制 Agent / Skill' },
                  { num: '10 工作日', label: '标杆交付周期（可调）' },
                  { num: '3 年+', label: 'AI 工程一线实践' },
                ].map((stat) => (
                  <div key={stat.label} className="bg-white/5 rounded-xl p-5 border border-white/10">
                    <p className="text-lg md:text-xl font-bold text-white mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>{stat.num}</p>
                    <p className="text-xs text-white/50" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 text-center">
          <FadeIn>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-4" style={{ letterSpacing: '-0.01em' }}>
              想让团队真正用上 AI？
            </h2>
            <p className="text-base text-[#64748B] mb-8 max-w-lg mx-auto">
              先聊聊你的团队现状，我们来判断哪种方式最适合你。首次沟通完全免费。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contact"
                className="px-7 py-3.5 rounded-full bg-[#F97316] text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-[#ea6c0a] transition-colors"
              >
                预约免费诊断
              </Link>
              <Link
                to="/#premise"
                className="px-7 py-3.5 rounded-full border border-slate-200 text-[#64748B] text-sm font-medium whitespace-nowrap cursor-pointer hover:border-slate-300 transition-colors"
              >
                了解方法论
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      <Footer />
    </div>
  );
}
